import React, { useState, useEffect, useRef } from 'react';
import '../Css/AdminCareers.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faHeading,
  faLink,
  faAlignLeft,
  faFileAlt,
  faMapMarkerAlt,
  faClock,
  faCalendar,
  faImage,
  faEye,
  faExclamationTriangle,
  faFolderOpen,
  faWallet,
  faPause,
  faBuilding,
  faListAlt,
  faUsers,
  faCalendarAlt,
  faCheckCircle,
  faPauseCircle,
  faDollarSign,
  faGraduationCap,
  faLightbulb,
  faTasks,
  faUserTie,
  faClipboardList
} from '@fortawesome/free-solid-svg-icons';

export default function AdminCareers() {
  const [careers, setCareers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    description: '',
    requirements: '',
    responsibilities: '',
    department: '',
    location: '',
    job_type: 'full-time',
    experience_level: 'entry',
    min_experience: 0,
    max_experience: '',
    salary_range: '',
    skills: '',
    status: 'open',
    application_deadline: '',
    posted_by: '',
    featured_image: null
  });

  const jobTypeOptions = [
    { value: 'full-time', label: 'Full Time', icon: faBriefcase, color: '#10b981' },
    { value: 'part-time', label: 'Part Time', icon: faClock, color: '#f59e0b' },
    { value: 'contract', label: 'Contract', icon: faFileAlt, color: '#3b82f6' },
    { value: 'internship', label: 'Internship', icon: faGraduationCap, color: '#8b5cf6' }
  ];

  const experienceLevelOptions = [
    { value: 'entry', label: 'Entry Level', icon: faGraduationCap, color: '#10b981' },
    { value: 'mid', label: 'Mid Level', icon: faUserTie, color: '#3b82f6' },
    { value: 'senior', label: 'Senior Level', icon: faBriefcase, color: '#8b5cf6' },
    { value: 'lead', label: 'Lead/Manager', icon: faUsers, color: '#ef4444' }
  ];

  const statusOptions = [
    { value: 'open', label: 'Open', icon: faCheckCircle, color: '#10b981' },
    { value: 'closed', label: 'Closed', icon: faPauseCircle, color: '#6b7280' },
    { value: 'on-hold', label: 'On Hold', icon: faPause, color: '#f59e0b' }
  ];

  const getSkillsArray = (skills) => {
    if (!skills) return [];
    
    try {
      if (Array.isArray(skills)) return skills;
      
      if (typeof skills === 'string') {
        if (skills.trim().startsWith('[')) {
          return JSON.parse(skills);
        } else {
          return skills.split(',').map(skill => skill.trim()).filter(skill => skill);
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error parsing skills:', error);
      if (typeof skills === 'string') {
        return skills.split(',').map(skill => skill.trim()).filter(skill => skill);
      }
      return [];
    }
  };

  useEffect(() => {
    fetchCareers();
    fetchUsers();
  }, []);

  const fetchCareers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/career.php/get`);
      const data = await response.json();
      if (data.success) {
        setCareers(data.careers);
      }
    } catch (error) {
      console.error('Error fetching careers:', error);
      toast.error('Error fetching careers');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/user.php/get`);
      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleOpenModal = (career = null) => {
    if (career) {
      setEditMode(true);
      setSelectedCareer(career);
      
      let existingFeaturedImage = null;
      if (career.featured_image_url) {
        existingFeaturedImage = career.featured_image_url;
      }

      let skillsDisplay = '';
      if (career.skills) {
        const skillsArray = getSkillsArray(career.skills);
        skillsDisplay = skillsArray.join(', ');
      }

      setFormData({
        title: career.title || '',
        slug: career.slug || '',
        description: career.description || '',
        requirements: career.requirements || '',
        responsibilities: career.responsibilities || '',
        department: career.department || '',
        location: career.location || '',
        job_type: career.job_type || 'full-time',
        experience_level: career.experience_level || 'entry',
        min_experience: career.min_experience || 0,
        max_experience: career.max_experience || '',
        salary_range: career.salary_range || '',
        skills: skillsDisplay,
        status: career.status || 'open',
        application_deadline: career.application_deadline || '',
        posted_by: career.posted_by || '',
        featured_image: null
      });

      setFeaturedImagePreview(existingFeaturedImage);

    } else {
      setEditMode(false);
      setSelectedCareer(null);
      setFormData({
        title: '',
        slug: '',
        description: '',
        requirements: '',
        responsibilities: '',
        department: '',
        location: '',
        job_type: 'full-time',
        experience_level: 'entry',
        min_experience: 0,
        max_experience: '',
        salary_range: '',
        skills: '',
        status: 'open',
        application_deadline: '',
        posted_by: '',
        featured_image: null
      });
      setFeaturedImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedCareer(null);
    setFeaturedImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      if (name === 'featured_image') {
        setFormData(prev => ({ ...prev, [name]: files[0] }));
        
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFeaturedImagePreview(e.target.result);
          };
          reader.readAsDataURL(files[0]);
        }
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeFeaturedImage = () => {
    setFormData(prev => ({ ...prev, featured_image: null }));
    setFeaturedImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    
    let skillsToSend = '';
    if (formData.skills) {
      const skillsArray = getSkillsArray(formData.skills);
      skillsToSend = JSON.stringify(skillsArray);
    }
    
    Object.keys(formData).forEach(key => {
      if (key !== 'featured_image' && key !== 'skills') {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      }
    });

    if (skillsToSend) {
      formDataToSend.append('skills', skillsToSend);
    }

    if (formData.featured_image) {
      formDataToSend.append('featured_image', formData.featured_image);
    }

    try {
      let url, method;
      if (editMode) {
        url = `${API_URL}/career.php/put/${selectedCareer.id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/career.php/post`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        body: formDataToSend
      });

      const data = await response.json();
      if (data.success) {
        fetchCareers();
        handleCloseModal();
        if (editMode) {
          toast.success('Career updated successfully!', { className: 'career-toast-edit' });
        } else {
          toast.success('Career added successfully!', { className: 'career-toast-add' });
        }
      } else {
        toast.error(data.error || 'An error occurred', { className: 'career-toast-error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form', { className: 'career-toast-error' });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/career.php/delete/${deleteId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchCareers();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('Career deleted successfully!', { className: 'career-toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'career-toast-error' });
      }
    } catch (error) {
      console.error('Error deleting career:', error);
      toast.error('An error occurred while deleting the career', { className: 'career-toast-error' });
    }
  };

  const getJobTypeInfo = (jobType) => {
    return jobTypeOptions.find(option => option.value === jobType) || jobTypeOptions[0];
  };

  const getExperienceLevelInfo = (level) => {
    return experienceLevelOptions.find(option => option.value === level) || experienceLevelOptions[0];
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="careers-admin-wrapper">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="careers-admin-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="careers-admin-title">
                <FontAwesomeIcon icon={faBriefcase} className="me-3" />
                Career Opportunities
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="careers-add-button" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Career
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-5">
        {loading ? (
          <div className="careers-loading-state">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : careers.length === 0 ? (
          <div className="careers-empty-state">
            <div className="careers-empty-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>
            <p className="careers-empty-text">No career opportunities found. Create your first job posting!</p>
          </div>
        ) : (
          <div className="row">
            {careers.map((career) => {
              const jobTypeInfo = getJobTypeInfo(career.job_type);
              const experienceInfo = getExperienceLevelInfo(career.experience_level);
              const statusInfo = getStatusInfo(career.status);
              const skillsArray = getSkillsArray(career.skills);
              
              return (
                <div key={career.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="career-display-card">
                    {/* Featured Image */}
                    {career.featured_image_url ? (
                      <div className="career-display-image">
                        <img 
                          src={`${API_URL}/${career.featured_image_url}`} 
                          alt={career.title} 
                        />
                      </div>
                    ) : (
                      <div className="career-display-image">
                        <div className="career-no-image">
                          <FontAwesomeIcon icon={faImage} />
                          <span>No Image</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="career-display-content">
                      <div className="career-header-section">
                        <h5 className="career-display-title">{career.title}</h5>
                      </div>

                      {career.department && (
                        <p className="career-display-department">
                          <FontAwesomeIcon icon={faBuilding} className="me-1" />
                          {career.department}
                        </p>
                      )}

                      {career.location && (
                        <p className="career-display-location">
                          <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                          {career.location}
                        </p>
                      )}

                      <p className="career-display-desc">
                        {career.description?.substring(0, 150) + '...'}
                      </p>
                      
                      {/* Skills */}
                      {skillsArray.length > 0 && (
                        <div className="mb-3">
                          <div className="career-skills-section">
                            <span className="career-skills-label">
                              <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                              Skills:
                            </span>
                            <div className="career-skills-tags">
                              {skillsArray.slice(0, 3).map((skill, index) => (
                                <span key={index} className="career-skill-tag">
                                  {skill}
                                </span>
                              ))}
                              {skillsArray.length > 3 && (
                                <span className="career-skill-tag more">
                                  +{skillsArray.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Experience & Salary */}
                      <div className="career-meta-info">
                        {(career.min_experience !== null || career.max_experience !== null) && (
                          <div className="career-info-item">
                            <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                            <span>
                              {career.min_experience || 0} - {career.max_experience || '∞'} years
                            </span>
                          </div>
                        )}
                        
                        {career.salary_range && (
                          <div className="career-info-item">
                            <FontAwesomeIcon icon={faWallet} className="me-1" />
                            <span>{career.salary_range}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Status Section */}
                    <div className="career-display-status">
                      <div className="career-status-badges">
                        <span 
                          className="career-status-badge"
                          style={{ 
                            background: `${jobTypeInfo.color}15`,
                            color: jobTypeInfo.color,
                            borderColor: `${jobTypeInfo.color}30`
                          }}
                        >
                          <FontAwesomeIcon icon={jobTypeInfo.icon} className="me-1" />
                          {jobTypeInfo.label}
                        </span>
                        
                        <span 
                          className="career-status-badge"
                          style={{ 
                            background: `${experienceInfo.color}15`,
                            color: experienceInfo.color,
                            borderColor: `${experienceInfo.color}30`
                          }}
                        >
                          <FontAwesomeIcon icon={experienceInfo.icon} className="me-1" />
                          {experienceInfo.label}
                        </span>

                        <span 
                          className="career-status-badge"
                          style={{ 
                            background: `${statusInfo.color}15`,
                            color: statusInfo.color,
                            borderColor: `${statusInfo.color}30`
                          }}
                        >
                          <FontAwesomeIcon icon={statusInfo.icon} className="me-1" />
                          {statusInfo.label}
                        </span>
                        
                        {career.application_deadline && (
                          <span className="career-deadline-badge">
                            <FontAwesomeIcon icon={faCalendar} className="me-1" />
                            Apply by: {formatDate(career.application_deadline)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="career-display-footer">
                      <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={() => handleOpenModal(career)}
                        title="Edit Career"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDeleteClick(career.id)}
                        title="Delete Career"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Career Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="career-modal-content modal-content">
              <div className="career-modal-header modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Career' : 'Add New Career'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="career-modal-body modal-body">
                <form onSubmit={handleSubmit} id="careerForm">
                  <div className="row">
                    {/* Basic Information Section */}
                    <div className="col-12 mb-4">
                      <h6 className="career-section-title">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Basic Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Job Title <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="title" 
                        value={formData.title} 
                        onChange={handleInputChange} 
                        placeholder="e.g. Senior React Developer"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="slug" 
                        value={formData.slug} 
                        onChange={handleInputChange} 
                        placeholder="senior-react-developer"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                        Department
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="department" 
                        value={formData.department} 
                        onChange={handleInputChange} 
                        placeholder="e.g. Engineering, Marketing"
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                        Location
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="location" 
                        value={formData.location} 
                        onChange={handleInputChange} 
                        placeholder="e.g. New York, Remote"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea 
                        className="career-textarea form-control" 
                        name="description" 
                        value={formData.description} 
                        onChange={handleInputChange} 
                        rows="4" 
                        placeholder="Detailed job description..."
                        required
                      ></textarea>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                        Requirements
                      </label>
                      <textarea 
                        className="career-textarea form-control" 
                        name="requirements" 
                        value={formData.requirements} 
                        onChange={handleInputChange} 
                        rows="4"
                        placeholder="List the job requirements..."
                      ></textarea>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faTasks} className="me-1" />
                        Responsibilities
                      </label>
                      <textarea 
                        className="career-textarea form-control" 
                        name="responsibilities" 
                        value={formData.responsibilities} 
                        onChange={handleInputChange} 
                        rows="4"
                        placeholder="List the key responsibilities..."
                      ></textarea>
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                        Skills
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="skills" 
                        value={formData.skills} 
                        onChange={handleInputChange} 
                        placeholder='React, Node.js, MongoDB (comma separated)'
                      />
                      <small className="text-muted">
                        Enter skills as comma-separated values
                      </small>
                    </div>

                    {/* Job Details Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="career-section-title">
                        <FontAwesomeIcon icon={faListAlt} className="me-2" />
                        Job Details
                      </h6>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faBriefcase} className="me-1" />
                        Job Type
                      </label>
                      <select 
                        className="career-input form-select" 
                        name="job_type" 
                        value={formData.job_type} 
                        onChange={handleInputChange}
                      >
                        {jobTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                        Experience Level
                      </label>
                      <select 
                        className="career-input form-select" 
                        name="experience_level" 
                        value={formData.experience_level} 
                        onChange={handleInputChange}
                      >
                        {experienceLevelOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Status
                      </label>
                      <select 
                        className="career-input form-select" 
                        name="status" 
                        value={formData.status} 
                        onChange={handleInputChange}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                        Min Experience (years)
                      </label>
                      <input 
                        type="number" 
                        className="career-input form-control" 
                        name="min_experience" 
                        value={formData.min_experience} 
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faGraduationCap} className="me-1" />
                        Max Experience (years)
                      </label>
                      <input 
                        type="number" 
                        className="career-input form-control" 
                        name="max_experience" 
                        value={formData.max_experience} 
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                        Salary Range
                      </label>
                      <input 
                        type="text" 
                        className="career-input form-control" 
                        name="salary_range" 
                        value={formData.salary_range} 
                        onChange={handleInputChange}
                        placeholder="e.g. $80k - $120k"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                        Application Deadline
                      </label>
                      <input 
                        type="date" 
                        className="career-input form-control" 
                        name="application_deadline" 
                        value={formData.application_deadline} 
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faUsers} className="me-1" />
                        Posted By <span className="text-danger">*</span>
                      </label>
                      <select 
                        className="career-input form-select" 
                        name="posted_by" 
                        value={formData.posted_by} 
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name || user.email}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Media Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="career-section-title">
                        <FontAwesomeIcon icon={faImage} className="me-2" />
                        Featured Image
                      </h6>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="career-form-label">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Upload Image
                      </label>
                      <input 
                        type="file" 
                        className="career-input form-control" 
                        name="featured_image" 
                        onChange={handleInputChange} 
                        accept="image/*"
                      />
                      
                      {featuredImagePreview && (
                        <div className="mt-3 position-relative">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Image Preview:
                          </h6>
                          <div className="position-relative d-inline-block">
                            <img 
                              src={`${API_URL}/${featuredImagePreview}`} 
                              alt="Featured preview"
                              className="career-featured-preview"
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={removeFeaturedImage}
                              title="Remove image"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </form>
              </div>
              <div className="career-modal-footer modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="careerForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Career' : 'Add Career'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="career-delete-header modal-header">
                <h5 className="modal-title text-white">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this career posting? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}